<template>
  <div class="bulk-job-detail">
    <div class="detail-header">
      <button @click="$emit('back')" class="back-button">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="back-icon">
          <line x1="19" y1="12" x2="5" y2="12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <polyline points="12,19 5,12 12,5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Indietro
      </button>
      <h2 class="detail-title">{{ job.job_name || job.original_filename }}</h2>
    </div>

    <!-- Progress Section -->
    <BulkJobProgress
      :status="job.status"
      :total-count="job.total_count"
      :completed-count="job.completed_count"
      :failed-count="job.failed_count"
      :loading="loading"
      @pause="handlePause"
      @resume="handleResume"
      @cancel="handleCancel"
      @download="handleDownload"
    />

    <!-- Job Info -->
    <div class="info-grid">
      <div class="info-item">
        <span class="info-label">File originale</span>
        <span class="info-value">{{ job.original_filename || '-' }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Creato il</span>
        <span class="info-value">{{ formatDate(job.created_at) }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Iniziato il</span>
        <span class="info-value">{{ formatDate(job.started_at) || 'Non ancora avviato' }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Completato il</span>
        <span class="info-value">{{ formatDate(job.completed_at) || 'In corso...' }}</span>
      </div>
    </div>

    <!-- Analyses Table -->
    <div class="analyses-section">
      <div class="analyses-header">
        <h3>Analisi</h3>
        <div class="filter-buttons">
          <button
            @click="filterStatus = null"
            class="filter-btn"
            :class="{ active: filterStatus === null }"
          >
            Tutte ({{ job.total_count }})
          </button>
          <button
            @click="filterStatus = 'completed'"
            class="filter-btn"
            :class="{ active: filterStatus === 'completed' }"
          >
            Completate ({{ job.completed_count }})
          </button>
          <button
            @click="filterStatus = 'failed'"
            class="filter-btn"
            :class="{ active: filterStatus === 'failed' }"
          >
            Fallite ({{ job.failed_count }})
          </button>
          <button
            @click="filterStatus = 'queued'"
            class="filter-btn"
            :class="{ active: filterStatus === 'queued' }"
          >
            In attesa
          </button>
        </div>
      </div>

      <div v-if="loadingAnalyses" class="loading-analyses">
        <div class="spinner"></div>
        <p>Caricamento analisi...</p>
      </div>

      <div v-else-if="analyses.length === 0" class="no-analyses">
        <p>Nessuna analisi trovata con i filtri selezionati.</p>
      </div>

      <div v-else class="analyses-table-container">
        <table class="analyses-table">
          <thead>
            <tr>
              <th>Azienda</th>
              <th>Stato</th>
              <th>Community</th>
              <th>Storytelling</th>
              <th>Experience</th>
              <th>Errore</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="analysis in analyses"
              :key="analysis.analysis_id"
              class="analysis-row"
              :class="'analysis-row--' + analysis.status"
            >
              <td class="company-name">{{ analysis.company_name }}</td>
              <td>
                <span class="status-badge" :class="'status-badge--' + analysis.status">
                  {{ getStatusLabel(analysis.status) }}
                </span>
              </td>
              <td class="score-cell">
                {{ getScore(analysis.community_score, 'community_score') }}
              </td>
              <td class="score-cell">
                {{ getScore(analysis.storytelling_score, 'storytelling_score') }}
              </td>
              <td class="score-cell">
                {{ getScore(analysis.experience_score, 'experience_score') }}
              </td>
              <td class="error-cell">
                {{ analysis.error_message ? truncateError(analysis.error_message) : '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination for analyses -->
      <div v-if="totalAnalysesPages > 1" class="pagination">
        <button
          @click="loadAnalyses(currentAnalysesPage - 1)"
          :disabled="currentAnalysesPage === 1"
          class="pagination-btn"
        >
          &laquo;
        </button>
        <span class="pagination-info">
          {{ currentAnalysesPage }} / {{ totalAnalysesPages }}
        </span>
        <button
          @click="loadAnalyses(currentAnalysesPage + 1)"
          :disabled="currentAnalysesPage === totalAnalysesPages"
          class="pagination-btn"
        >
          &raquo;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useBulkAnalysisStore } from '@/stores/bulkAnalysisStore';
import { useAuthStore } from '@/stores/authStore';
import BulkJobProgress from './BulkJobProgress.vue';

export default {
  name: 'BulkJobDetail',
  components: {
    BulkJobProgress,
  },
  props: {
    job: {
      type: Object,
      required: true,
    },
  },
  emits: ['back', 'updated'],
  data() {
    return {
      loading: false,
      loadingAnalyses: false,
      analyses: [],
      filterStatus: null,
      currentAnalysesPage: 1,
      analysesPerPage: 50,
      totalAnalyses: 0,
    };
  },
  computed: {
    totalAnalysesPages() {
      return Math.ceil(this.totalAnalyses / this.analysesPerPage);
    },
    userId() {
      const authStore = useAuthStore();
      return authStore.userId;
    },
  },
  watch: {
    filterStatus() {
      this.currentAnalysesPage = 1;
      this.loadAnalyses();
    },
    'job.job_id': {
      immediate: true,
      handler() {
        this.loadAnalyses();
      },
    },
  },
  methods: {
    async loadAnalyses(page = 1) {
      this.loadingAnalyses = true;
      this.currentAnalysesPage = page;

      try {
        const bulkStore = useBulkAnalysisStore();
        const offset = (page - 1) * this.analysesPerPage;

        const result = await bulkStore.fetchBulkJobAnalyses(
          this.job.job_id,
          this.userId,
          {
            status: this.filterStatus,
            limit: this.analysesPerPage,
            offset,
          }
        );

        this.analyses = result.analyses;
        this.totalAnalyses = result.total;
      } catch (error) {
        console.error('Error loading analyses:', error);
      } finally {
        this.loadingAnalyses = false;
      }
    },

    async handlePause() {
      this.loading = true;
      try {
        const bulkStore = useBulkAnalysisStore();
        await bulkStore.pauseJob(this.job.job_id, this.userId);
        this.$emit('updated');
      } catch (error) {
        console.error('Error pausing job:', error);
      } finally {
        this.loading = false;
      }
    },

    async handleResume() {
      this.loading = true;
      try {
        const bulkStore = useBulkAnalysisStore();
        await bulkStore.resumeJob(this.job.job_id, this.userId);
        this.$emit('updated');
      } catch (error) {
        console.error('Error resuming job:', error);
      } finally {
        this.loading = false;
      }
    },

    async handleCancel() {
      if (!confirm('Sei sicuro di voler annullare questo job? Le analisi in attesa verranno cancellate.')) {
        return;
      }

      this.loading = true;
      try {
        const bulkStore = useBulkAnalysisStore();
        await bulkStore.cancelJob(this.job.job_id, this.userId);
        this.$emit('updated');
      } catch (error) {
        console.error('Error cancelling job:', error);
      } finally {
        this.loading = false;
      }
    },

    async handleDownload() {
      try {
        const bulkStore = useBulkAnalysisStore();
        await bulkStore.downloadResults(this.job.job_id, this.userId);
      } catch (error) {
        console.error('Error downloading results:', error);
        alert('Errore durante il download dei risultati.');
      }
    },

    getStatusLabel(status) {
      const labels = {
        queued: 'In coda',
        processing: 'In corso',
        completed: 'Completata',
        failed: 'Fallita',
      };
      return labels[status] || status;
    },

    getScore(scoreObj, key) {
      if (!scoreObj || typeof scoreObj !== 'object') return '-';
      const score = scoreObj[key];
      if (score === undefined || score === null) return '-';
      return typeof score === 'number' ? score.toFixed(2) : score;
    },

    truncateError(error) {
      if (!error) return '-';
      return error.length > 50 ? error.substring(0, 50) + '...' : error;
    },

    formatDate(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.bulk-job-detail {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.5rem;
  transition: color 0.2s ease;
}

.back-button:hover {
  color: #2e4eed;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.detail-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 500;
}

/* Analyses Section */
.analyses-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.analyses-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 1rem;
}

.analyses-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-btn:hover {
  border-color: #2e4eed;
}

.filter-btn.active {
  background: #2e4eed;
  border-color: #2e4eed;
  color: white;
}

.loading-analyses,
.no-analyses {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6b7280;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #2e4eed;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 0.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.analyses-table-container {
  overflow-x: auto;
}

.analyses-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.analyses-table th,
.analyses-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.analyses-table th {
  background: white;
  font-weight: 600;
  color: #6b7280;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.analysis-row--failed {
  background: #fef2f2;
}

.company-name {
  font-weight: 500;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
}

.status-badge--queued {
  background: #f3f4f6;
  color: #6b7280;
}

.status-badge--processing {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-badge--completed {
  background: #d1fae5;
  color: #059669;
}

.status-badge--failed {
  background: #fee2e2;
  color: #dc2626;
}

.score-cell {
  font-family: monospace;
  text-align: center;
}

.error-cell {
  color: #dc2626;
  font-size: 0.75rem;
  max-width: 150px;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.pagination-btn {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  background: white;
  border: 1px solid #2e4eed;
  color: #2e4eed;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #2e4eed;
  color: white;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
  padding: 0 0.5rem;
}

@media (max-width: 768px) {
  .detail-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .analyses-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-buttons {
    width: 100%;
  }

  .filter-btn {
    flex: 1;
    text-align: center;
  }
}
</style>
